import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {Order} from '@app/checkout/models/order-request.model';
import {updateOrderFromConfirmOrder} from '@app/checkout/state/checkout.actions';
import {selectCheckoutStatus, selectOrderForm} from '@app/checkout/state/checkout.selector';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {DateFormat} from '@app/shared/constants';
import {StepperService} from '@app/shared/services/stepper.service';
import {Store} from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {ConfirmationService} from 'primeng/api';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { Divider } from 'primeng/divider';
import { Checkbox } from 'primeng/checkbox';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-order-confirm',
    templateUrl: './order-confirm.component.html',
    styleUrls: ['./order-confirm.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        Divider,
        Checkbox,
        DatePipe,
        TranslatePipe,
    ],
})
export class OrderConfirmComponent implements OnDestroy, ICheckoutComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly stepperService = inject(StepperService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);

  orderConfirmForm: FormGroup<{patientConsent: FormControl<boolean | null>}>;
  checkoutState: ECheckoutStatus | undefined;
  orderData: Order | undefined;
  private readonly unsubscribe$ = new Subject<void>();
  checkoutState$: Observable<ECheckoutStatus>;
  checkoutStatus = ECheckoutStatus;
  dateFormat = DateFormat;
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;

  constructor() {
    this.orderConfirmForm = this.formBuilder.group({
      patientConsent: [false, Validators.requiredTrue],
    });

    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
          this.orderConfirmForm.patchValue({
            patientConsent: this.orderData.doctorConsentAccepted,
          });
        })
      )
      .subscribe();

    this.checkoutState$ = this.store.select(selectCheckoutStatus);
    this.checkoutState$.pipe(
      takeUntil(this.unsubscribe$),
      tap(checkoutStatus => {
        if (
          checkoutStatus === ECheckoutStatus.SuccessFromConfirmOrder ||
          checkoutStatus === ECheckoutStatus.ErrorFromConfirmOrder
        ) {
          this.nextInProgress = false;
          this.draftInProgress = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getOrderItemAttribute(key: string) {
    if (this.orderData?.orderItems?.length && this.orderData?.orderItems[0].orderItemAttributes?.length) {
      return ObjectUtils.getValueByDotSeparatedKey(key, this.orderData?.orderItems[0].orderItemAttributes[0]);
    }
    return '';
  }

  onSubmit() {
    const orderRequest = {
      ...this.orderData,
      productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
      doctorConsentAccepted: this.orderConfirmForm.controls.patientConsent.value ?? false,
    } as Order;

    this.store.dispatch(updateOrderFromConfirmOrder({orderRequest: orderRequest}));
  }

  disableNext() {
    return this.orderConfirmForm.invalid;
  }

  next() {
    this.nextInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.Confirmed.toString(),
      };
    }
    this.onSubmit();
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.ConsentInfo);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    this.draftInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.New.toString(),
      };
    }
    this.onSubmit();
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
